import { AvatarIcon, Icon } from '@civilgrid/ui/components';
import { type ReactNode, useMemo } from 'react';
import type { Activity } from '~/common/types';
import { useScrollTo } from '~/hooks/useScrollTo';
import DateLabel from '../../common/DateLabel';
import * as Item from './Activities';

interface Props {
  activities: Activity[];
}

export default function ActivityList({ activities }: Props) {
  const { ref: scrollRef } = useScrollTo([activities]);

  const activityItems = useMemo(() => {
    return activities.map((a) => {
      return (
        <div
          key={`civilsearch-activity-${a.id}`}
          className="relative flex w-full items-center justify-between space-x-2 border-b p-2 text-sm"
        >
          <div>
            {a.user ? (
              <AvatarIcon user={{ name: a.user.name, email: a.user.email }} aria-hidden />
            ) : (
              <span aria-hidden className="flex h-7 w-7 items-center justify-center rounded-full bg-grey-20">
                <Icon icon="person" />
              </span>
            )}
          </div>
          <Details a={a} />
          <DateLabel date={a.createdAt?.toString()} />
        </div>
      );
    });
  }, [activities]);

  let content: ReactNode = (
    <div className="h-full pt-48 text-center">
      Nothing here yet! As soon as you start making data requests, you'll see updates here.
    </div>
  );

  if (activityItems.length > 0) {
    content = activityItems;
  }

  return (
    <div className="relative flex h-full w-full grow flex-col space-y-4 overflow-y-auto rounded-md border p-2">
      {content}
      <div ref={scrollRef} />
    </div>
  );
}

interface ActivityProps {
  a: Activity;
}

export const activityMap: {
  [key: string]: ({ activity }: { activity: Activity; compact?: boolean; includeAgencyName?: boolean }) => ReactNode;
} = {
  projectCreated: Item.ProjectCreated,
  projectBoundaryUpdated: Item.BoundaryUpdate,
  mapsRequested: Item.MapsRequested,
  asbuiltsRequested: Item.AsbuiltsRequested,
  dataRequestUpdated: Item.Updated,
  supportingFileAdded: Item.SupportingFileAdded,
  supportingFileDeleted: Item.SupportingFileDeleted,
  dataRequestFileAdded: Item.RequestFileAdded,
  dataRequestFileDeleted: Item.RequestFileDeleted,
  requestUpdateAdded: Item.UpdateAdded,
  requestUpdateDeleted: Item.UpdateDeleted,
};

const Details = ({ a }: ActivityProps) => {
  // system-generated activities (e.g. the PG&E cloud sync job) have no userId
  const isCloudSync = a.details?.source === 'pgeCloudSync';
  const name = <span>{a.user?.name || (isCloudSync ? 'CivilGrid via Cloud Sync' : 'A user')} </span>;
  const Comp = activityMap[a.activityType];

  return (
    <div className="relative w-full">
      {name}
      {Comp && <Comp activity={a} />}
    </div>
  );
};
